import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Button, Glyphicon } from 'react-bootstrap';
import classnames from 'classnames';
import _ from 'underscore';
import { capitalize, titleize } from 'underscore.string';

import CargomaticInput from 'components/v2/input';
import CargomaticModal from 'components/v2/modal';
import { withTranslation } from 'react-i18next';
import formatMoney from 'utils/formatters/Money';
import { sluggify } from 'utils/localization/utils';
import { getMaxAdjustmentRate } from '../../../../helpers/order-shipments';

const adjustmentMessages = {
  invalid_adjustment: 'ADJUSTMENT_MESSAGES_INVALID_ADJUSTMENT',
  large_adjustment: 'ADJUSTMENT_MESSAGES_LARGE_ADJUSTMENT',
  max_internal_adjustment: 'ADJUSTMENT_MESSAGES_INTERNAL_ADJUSTMENT',
  mile_band_adjustment: 'ADJUSTMENT_MESSAGES_BAND_ADJUSTMENT',
};

// const MIN_COST_PER_MILE = 5.0;
// const MAX_COST_PER_MILE = 20.0;

class AddShorthaulAdjustmentModal extends Component {
  static propTypes = {
    item: PropTypes.shape({
      message: PropTypes.string,
      type: PropTypes.string,
      equipment: PropTypes.string,
      value: PropTypes.number,
    }),
    items: PropTypes.array,
    ledger: PropTypes.oneOf(['receivables', 'payables']),
    type: PropTypes.string,
    show: PropTypes.bool.isRequired,
    onAddAdjustment: PropTypes.func.isRequired,
    onHide: PropTypes.func.isRequired,
    mileDistance: PropTypes.number.isRequired,
    shipmentType: PropTypes.string.isRequired,
    currency: PropTypes.string.isRequired,
  };

  constructor(props) {
    super(props);
    this.state = this.getInitialState();
  }

  getInitialState() {
    return {
      errors: {
        adjustmentAmount: {},
        adjustmentReason: {},
        adjustmentNote: {},
      },
      adjustmentValue: 0,
      adjustmentReasonCode: '',
      adjustmentInvoiceNote: '',
      isValidAdjustment: false,
      disableAddAdjustment: false,
      renderWarning: false,
    };
  }

  onHide() {
    return () => {
      this.setState(this.getInitialState());
      this.props.onHide();
    };
  }

  getItemRate() {
    return (this.props.items || [])
      .filter((item) => {
        return item.adjusted_item === this.props.item.type;
      })
      .map((item) => {
        return item.value || 0;
      })
      .reduce((a, b) => {
        return a + b;
      }, this.props.item.value || 0);
  }

  getAdjustmentComponent() {
    const { type, t, currency } = this.props;
    const { errors } = this.state;
    const { adjustmentAmount } = errors;

    const adjustmentValueClassnames = classnames({
      'text-green': _.isEmpty(adjustmentAmount),
      'text-red-500': !_.isEmpty(adjustmentAmount),
      'font-bold': 1,
      'px-1': 1,
    });

    return (
      (<div data-testid="div-a8b9539d" className="w-full p-4">
        <CargomaticInput
          dataTestId="CargomaticInput-47772872"
          className="mb-4"
          error={errors.adjustmentAmount}
          label={t('ADJUSTED_AMOUNT')}
          info={t('INTENDED_VALUE_POST_ADJUSTMENT')}
          onChange={this.handleAdjustmentValue}
          maxLength={5}
          type="number"
          value={this.state.adjustmentValue} />
        <CargomaticInput
          dataTestId="CargomaticInput-61bb2ac6"
          className="mb-4"
          error={errors.adjustmentReason}
          label={t('ADJUSTMENT_REASON')}
          info={t('ADJUSTMENT_REASON_INFO')}
          onChange={this.handleAdjustmentReason}
          value={this.state.adjustmentReasonCode} />
        {type === 'customer' && (
          <CargomaticInput
            dataTestId="CargomaticInput-22747f14"
            className="mb-4"
            error={errors.adjustmentNote}
            label={t('INVOICE_NOTE')}
            info={t('INVOICE_NOTE_INFO')}
            onChange={this.handleAdjustmentNote}
            maxLength={50}
            value={this.state.adjustmentNote} />
        )}
        <div data-testid="div-76495682" className="flex">
          <div data-testid="div-15eb50ad" className="w-1/2 p-2">
            <div data-testid="div-58d3aaf0" className="flex justify-end">
              <span data-testid="span-d860fc2a" className="text-grey-darker">{t('CURRENT_VALUE')}</span>
              <span data-testid="span-e4b5be8d" className="px-1 font-bold text-red-500">
                {formatMoney(this.getTotal(), currency)}
              </span>
            </div>
            <div data-testid="div-c78a83b4" className="flex justify-end">
              <span data-testid="span-a0fc6f38" className="text-grey-darker">{t('CURRENT_TOTAL')}</span>
              <span data-testid="span-78592b6b" className="px-1 font-bold text-red-500">
                {formatMoney(this.getOverallTotal(), currency)}
              </span>
            </div>
          </div>
          <div data-testid="div-ce0100d7" className="w-1/2 p-2">
            <div data-testid="div-36313c28" className="flex justify-end">
              <span data-testid="span-022edb81" className="text-grey-darker">{t('ADJUSTMENT_VALUE')}</span>
              {type === 'customer' && (
                <span data-testid="span-ee82ac40" className={adjustmentValueClassnames}>
                  {this.state.adjustmentValue
                    ? formatMoney(this.state.adjustmentValue, currency)
                    : formatMoney(0, currency)}
                </span>
              )}
              {type === 'internal' && (
                <span data-testid="span-661a97ce" className={adjustmentValueClassnames}>
                  {this.state.adjustmentValue ||
                  this.state.adjustmentValue === 0
                    ? formatMoney(
                        this.state.adjustmentValue - this.getTotal(),
                        currency,
                      )
                    : formatMoney(0, currency)}
                </span>
              )}
            </div>
            <div data-testid="div-44a5de42" className="flex justify-end">
              <span data-testid="span-497af24a" className="text-grey-darker">
                {t('TOTAL_AFTER_ADJUSTMENT')}
              </span>
              <span data-testid="span-8f79a334" className="px-1 font-bold text-green">
                {this.state.adjustmentValue === 0 ||
                this.state.adjustmentValue === '' ||
                this.state.adjustmentValue === null ||
                Number.isNaN(this.state.adjustmentValue)
                  ? formatMoney(this.getOverallTotal(), currency)
                  : formatMoney(this.getTotalAfterAdjustment(), currency)}
              </span>
            </div>
          </div>
        </div>
      </div>)
    );
  }

  getAdjustmentFooter() {
    const { t } = this.props;
    return (
      (<div data-testid="div-62db7d75" className="flex justify-end">
        <button
          data-testid="button-324982f6"
          type="button"
          className="px-2 button button-secondary"
          onClick={this.onHide()}>
          {titleize(t('CLOSE'))}
        </button>
        <button
          data-testid="button-f5b738b6"
          type="button"
          className="button button-green"
          disabled={!this.isValid()}
          onClick={this.handleAddAdjustment}>
          {titleize(t('SAVE'))}
        </button>
      </div>)
    );
  }

  getTotal() {
    const { items, type, item } = this.props;
    return items.reduce(
      (x, y) => {
        if (type === 'internal') {
          if (
            y.type === 'internal_adjustment' &&
            y.message.includes(item.message)
          ) {
            return x + (y.value || 0);
          }
          return x;
        }
        if (
          y.type === 'customer_adjustment' &&
          y.adjusted_item.includes(item.category)
        ) {
          return x + (y.value || 0);
        }
        return x;
      },
      type === 'internal' ? item.value : 0,
    );
  }

  getOverallTotal() {
    const { items } = this.props;
    if (!items || items.length === 0) {
      return 0;
    }
    return items.reduce((x, y) => {
      return x + (y.value || 0);
    }, 0);
  }

  getTotalAfterAdjustment() {
    const { type } = this.props;
    const sum = this.getOverallTotal();
    const { adjustmentValue } = this.state;

    if (adjustmentValue === 0 || !adjustmentValue) {
      return sum - this.getItemRate();
    }

    const adjustment =
      type === 'customer'
        ? sum + (parseFloat(adjustmentValue) || 0)
        : sum + (parseFloat(adjustmentValue || 0) - this.getTotal());

    return adjustment;
  }

  handleAdjustmentReason = (reason) => {
    const { t } = this.props;
    const { errors } = this.state;
    let adjustmentReasonCode = '';

    if (reason && reason !== '') {
      adjustmentReasonCode = reason;
      errors.adjustmentReason = {};
    } else {
      errors.adjustmentReason = {
        message: t('ADJUSTMENT_REASON_ERROR'),
      };
    }

    this.setState({ adjustmentReasonCode, errors });
  };

  handleAdjustmentValue = (value) => {
    const { t } = this.props;
    let adjustmentValue;
    const { errors } = this.state;

    const parsedValue = parseFloat((value || '').trim());

    if (parsedValue !== 0 && !parsedValue) {
      errors.adjustmentAmount = {
        message: t('ADJUSTMENT_VALUE_ERROR'),
      };
    } else {
      adjustmentValue = parsedValue;
      errors.adjustmentAmount = {};
    }

    this.setState({ adjustmentValue, errors }, () => {
      this.validateMaxAdjustmentValue();
    });
  };

  handleAdjustmentNote = (note) => {
    const { t } = this.props;
    const { errors } = this.state;
    let adjustmentInvoiceNote = '';

    if (note && note !== '') {
      adjustmentInvoiceNote = note;
      errors.adjustmentNote = {};
    } else {
      errors.adjustmentNote = {
        message: t('INVOICE_NOTE_ERROR'),
      };
    }
    this.setState({ adjustmentInvoiceNote, errors });
  };

  handleAddAdjustment = () => {
    const { adjustmentValue, adjustmentReasonCode, adjustmentInvoiceNote } =
      this.state;
    const { type, t } = this.props;

    let error = false;
    if (adjustmentValue !== 0 && !adjustmentValue) {
      return this.handleAdjustmentValue(null);
    }

    if (
      !adjustmentReasonCode ||
      (adjustmentReasonCode && adjustmentReasonCode.trim() === '')
    ) {
      error = true;
      return this.handleAdjustmentReason('');
    }

    if (
      type === 'customer' &&
      adjustmentInvoiceNote &&
      adjustmentInvoiceNote.trim() === ''
    ) {
      error = true;
      this.handleAdjustmentNote('');
    }

    // All validation runs above
    if (error) return;

    let value;
    if (type === 'customer') {
      value = adjustmentValue;
    } else {
      value = (parseFloat(adjustmentValue) || 0) - this.getTotal();
    }

    value = value !== 0 ? value.toFixed(2) : value;

    const adjustment =
      type === 'internal'
        ? {
            value,
            message: t('ADJUSTMENT_FOR_MESSAGE', {
              message: this.props.item.message,
            }),
            adjustedItem: this.props.item.type,
            type: 'internal_adjustment',
            reason: this.state.adjustmentReasonCode,
            category: 'rate_adjustments',
            silent: true,
            processed: false,
            equipment: this.props.item.equipment,
          }
        : {
            value,
            message: this.state.adjustmentInvoiceNote,
            adjustedItem: this.props.item.type,
            type: 'customer_adjustment',
            reason: this.state.adjustmentReasonCode,
            category: 'rate_adjustments',
            silent: false,
            equipment: this.props.item.equipment,
            processed: false,
          };

    this.props.onAddAdjustment(adjustment);
    this.setState(this.getInitialState());
    this.props.onHide();
  };

  errorsExist() {
    const { errors } = this.state;
    const keys = Object.keys(errors);
    let exists = false;
    for (let i = 0; i < keys.length; i++) {
      const key = keys[i];
      if (!_.isEmpty(errors[key])) {
        exists = true;
        break;
      }
    }
    return exists;
  }

  isValid() {
    const { adjustmentValue, adjustmentReasonCode, adjustmentInvoiceNote } =
      this.state;
    const { type } = this.props;

    const errors = this.errorsExist();
    if (errors) return false;

    if (type === 'customer') {
      return adjustmentValue && adjustmentReasonCode && adjustmentInvoiceNote;
    }
    return adjustmentValue !== null && adjustmentReasonCode !== '';
  }

  validateMaxAdjustmentValue() {
    const { t } = this.props;
    const { adjustmentValue, errors } = this.state;
    const mileDistance = this.props.mileDistance;

    if (adjustmentValue) {
      if (this.props.shipmentType === 'dayrate') {
        if (parseFloat(adjustmentValue) > 5000) {
          errors.adjustmentAmount = {
            message: t('MAX_SINGLE_ADJUSTMENT_AMOUNT_EXCEEDED'),
          };
          this.setState({
            warning: 'max_internal_adjustment',
            disableAddAdjustment: true,
            renderWarning: true,
            errors,
          });
        } else {
          this.setState({
            warning: null,
            disableAddAdjustment: false,
            renderWarning: false,
          });
        }
      } else if (
        parseFloat(adjustmentValue) / (mileDistance || 1) >
        getMaxAdjustmentRate(mileDistance)
      ) {
        errors.adjustmentAmount = {
          message: t('MAX_ADJUSTMENT_AMOUNT_EXCEEDED'),
        };
        this.setState({
          warning: 'mile_band_adjustment',
          disableAddAdjustment: true,
          renderWarning: true,
          errors,
        });
      } else {
        this.setState({
          warning: null,
          disableAddAdjustment: false,
          renderWarning: false,
        });
      }
    }
  }

  getModalTitle() {
    const {
      item: { message },
      ledger,
      type,
      t,
    } = this.props;
    let title = '';
    if (type === 'customer' && ledger === 'receivables') {
      title = t('CUSTOMER_SHIPPER_ADJUSTMENT', {
        message: t(sluggify(message)),
      });
    } else if (type === 'customer' && ledger !== 'receivables') {
      title = t('CUSTOMER_CARRIER_ADJUSTMENT', {
        message: t(sluggify(message)),
      });
    } else if (type === 'internal' && ledger === 'receivables') {
      title = t('INTERNAL_SHIPPER_ADJUSTMENT', {
        message: t(sluggify(message)),
      });
    } else {
      title = t('INTERNAL_CARRIER_ADJUSTMENT', {
        message: t(sluggify(message)),
      });
    }
    return title;
  }

  render() {
    const { show, t } = this.props;
    const title = this.getModalTitle();
    return (
      (<div data-testid="div-910ae50d">
        {show && (
          <CargomaticModal
            dataTestId="CargomaticModal-0d8497c1"
            titleClassNames="text-grey-darker px-4"
            title={title}
            show={show}
            body={this.getAdjustmentComponent()}
            footer={this.getAdjustmentFooter()}
            onHide={this.onHide()}
            location="middle">
            <Button onClick={this.onHide()}>{titleize(t('CLOSE'))}</Button>
            <Button
              bsStyle="primary"
              onClick={this.addAdjustment}
              disabled={this.state.disableAddAdjustment}
            >
              {titleize(t('SAVE'))}
            </Button>
          </CargomaticModal>
        )}
      </div>)
    );
  }
}

export default withTranslation()(AddShorthaulAdjustmentModal);
